/**
 * Artifact side panel (handoff §5.2) — 520px slide-over with four tabs.
 *
 * §5.2: "× closes panel (does not clear selectedArtifactId — reopening re-uses it)."
 * §5.2: "No approve/reassign buttons at the panel footer. Removed intentionally."
 */

import { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { X, Download, Upload } from 'lucide-react';
import { color, font, radius, shadow, OUTCOME_LABEL, OUTCOME_ORDER } from '../tokens';
import type { Outcome } from '../tokens';
import type { ArtifactDetail, PanelTab, TranslateTab, LogicFilter, Translation } from '../types';
import { OutcomePill, CatChip } from './Pills';
import TranslateBlock from './TranslateBlock';

const TABS: { id: PanelTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'lineage', label: 'Lineage' },
  { id: 'logic', label: 'Business logic' },
  { id: 'insights', label: 'Insights' },
];

const LOGIC_FILTERS: { id: LogicFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'hc', label: 'Hardcodes' },
  { id: 'fn', label: 'Rules' },
];

const SIGNAL_COLOR = { high: '#dc2626', medium: '#d97706', low: '#16a34a' } as const;

/** Server-rendered highlighted HTML → plain text, for the export report. */
function htmlToText(html: string): string {
  return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
}

/** Compose a high-level overview report of the artifact as Markdown (spec: "Export button — take
 *  everything produced and export a high-level overview"). Deterministic, client-side, no backend. */
function buildOverviewMarkdown(a: ArtifactDetail): string {
  const c = a.counts;
  const lines: string[] = [];
  lines.push(`# ${a.name}`, '');
  lines.push(`- **Owner:** ${a.owner}`);
  lines.push(`- **Disposition:** ${OUTCOME_LABEL[a.outcome]}${a.confidence ? ` (confidence ${a.confidence.toFixed(2)})` : ''}`);
  lines.push(`- **Timing category:** Cat ${a.timing_cat}`, '');
  lines.push('## Purpose', a.purpose || '—', '');
  lines.push('## Why this disposition', a.rationale || '—', '');
  if (a.sources.length || a.transform_count || a.targets.length) {
    lines.push('## Lineage');
    if (a.sources.length) lines.push(`- **Sources:** ${a.sources.join(', ')}`);
    if (a.transform_count) lines.push(`- **Transforms:** ${a.transform_count}`);
    if (a.targets.length) lines.push(`- **Targets:** ${a.targets.join(', ')}`);
    lines.push('');
  }
  lines.push('## Business logic');
  lines.push(`${c.formulas} formulas · ${c.macros} macros · ${c.hardcodes} hardcodes · ${c.lookups} lookups · ${c.distinct_rules} distinct rules`, '');
  for (const r of a.logic_rows) {
    lines.push(`### ${r.tag} · ${r.location}`, '```', htmlToText(r.code_html).trim(), '```', '');
  }
  return lines.join('\n');
}

// Per-outcome package shape (task 1218070214136694, subtask 8 — SOW Phase 3 spec package).
// The consultant exports the GCC-executable packet for ONE EUC; what goes in the packet differs by
// disposition outcome. Everything generated here is deterministic + client-side, and the spec /
// AC / test / runbook sections are marked DRAFT because they are seeded from lineage + business
// logic, not a validated build — honest about what's real vs. a starting point.
const PACKAGE_KIND: Record<string, { title: string; sections: string[] }> = {
  box1: { title: 'Migration Requirements Package — Full SDLC rebuild', sections: ['target', 'ac', 'tests', 'runbook'] },
  box2: { title: 'Migration Requirements Package — Governed / light SDLC', sections: ['target', 'ac', 'tests', 'runbook'] },
  box3: { title: 'Platform Migration Package — upstream owner', sections: ['target', 'ac', 'tests'] },
  box4: { title: 'Documentation Bundle — retain & improve', sections: ['target', 'runbook'] },
  consolidate: { title: 'Consolidation Package — merge into survivor', sections: ['target', 'ac'] },
  decommission: { title: 'Decommission Record — sunset', sections: ['runbook'] },
  queue: { title: 'Resolution Queue Item — unresolved', sections: [] },
};

function buildSpecPackageMarkdown(a: ArtifactDetail): string {
  const kind = PACKAGE_KIND[a.outcome] || PACKAGE_KIND.queue;
  const c = a.counts;
  const L: string[] = [];
  L.push(`# ${kind.title}`, '');
  L.push(`**EUC:** ${a.name}  `);
  L.push(`**Disposition:** ${OUTCOME_LABEL[a.outcome]} × Timing Cat ${a.timing_cat}${a.confidence ? ` · confidence ${a.confidence.toFixed(2)}` : ''}  `);
  L.push(`**Target state:** ${a.target_state_label || '—'}  `);
  L.push(`**Owner:** ${a.owner}`, '');
  L.push('> Draft — sections below are seeded from extracted lineage + business logic, not a validated build. Review before handing to a GCC pod.', '');

  L.push('## Why this box', a.rationale || '—', '');
  if (a.gates_fired?.length) {
    L.push('## Gates fired');
    for (const g of a.gates_fired) L.push(`- \`${g.id}\` — ${g.action}`);
    L.push('');
  }

  if (kind.sections.includes('target')) {
    L.push('## Target-state spec (Draft)');
    if (a.sources.length) L.push(`- **Inputs:** ${a.sources.join(', ')}`);
    if (a.targets.length) L.push(`- **Outputs:** ${a.targets.join(', ')}`);
    if (a.transform_count) L.push(`- **Transforms to reimplement:** ${a.transform_count}`);
    L.push(`- **Logic to port:** ${c.formulas} formulas · ${c.macros} macros · ${c.distinct_rules} distinct rules`, '');
    for (const r of a.logic_rows) L.push(`  - ${r.tag} · ${r.location}`);
    L.push('');
  }
  if (kind.sections.includes('ac')) {
    L.push('## Acceptance criteria (Draft)');
    L.push('- [ ] Outputs reconcile to the legacy EUC for one production period (tolerance stated).');
    if (a.targets.length) L.push(`- [ ] ${a.targets.join(', ')} match the legacy figures row-for-row.`);
    a.logic_rows.slice(0, 5).forEach((r) => L.push(`- [ ] Rule preserved: ${r.location}.`));
    L.push('- [ ] Controls / sign-off documented for the regulated path.', '');
  }
  if (kind.sections.includes('tests')) {
    L.push('## Test scenarios (Draft)');
    L.push('- Happy path: a representative period reproduces the legacy output.');
    L.push('- Boundary: each hardcoded threshold tested at, just below, and just above the cutoff.');
    L.push('- Regression: prior filed periods (e.g. Y-9C / FFIEC / FR Y-15 cadence) re-run and diffed.', '');
  }
  if (kind.sections.includes('runbook')) {
    L.push('## Runbook draft (Draft)');
    L.push(a.outcome === 'decommission'
      ? '1. Confirm zero active consumers.\n2. Snapshot + archive the final output.\n3. Redirect any references to the survivor / system of record.\n4. Record the decommission with date + approver.'
      : '1. Schedule / trigger.\n2. Inputs refreshed and validated.\n3. Run + reconcile to control totals.\n4. Publish outputs; log the run for audit.', '');
  }
  if (!kind.sections.length) {
    L.push('## Resolution needed', 'This EUC is unresolved (no owner, or a blocking dependency). Close the gap on Owner Attestation before a package can be produced.', '');
  }
  return L.join('\n');
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface Props {
  artifact: ArtifactDetail;
  panelTab: PanelTab;
  onPanelTab: (t: PanelTab) => void;
  translateTab: TranslateTab;
  onTranslateTab: (t: TranslateTab) => void;
  onClose: () => void;
  onToast: (msg: string) => void;
  fetchTranslation?: (id: string, target: TranslateTab, regenerate?: boolean) => Promise<Translation>;
  reviewTranslation?: (id: string, target: TranslateTab, current: Translation) => Promise<Translation>;
  onAttach?: (id: string) => void;
  // Single-EUC override (task 1218070214136694, subtask 8): committed via the SAME engine endpoint
  // as the Review Queue / Framework Mapping override (no duplicated commit logic). Undefined when
  // this artifact isn't a real inventory row (e.g. an ad-hoc analyzed upload with no euc_id).
  onOverride?: (eucId: string, outcome: Outcome, reason: string) => Promise<{ ok: boolean; error?: string }>;
  onPersistTranslation?: (runId: string, target: TranslateTab, translation: Translation) => void;
  /** True when a generation for THIS artifact + the currently-selected target is still running
   *  somewhere outside this component tree (tracked by the parent so it survives TranslateBlock
   *  unmounting on a panel-tab switch — Ryan 7/31 QA finding 2). */
  pendingTranslation?: boolean;
}

export default function ArtifactPanel({
  artifact, panelTab, onPanelTab, translateTab, onTranslateTab, onClose, onToast,
  fetchTranslation, reviewTranslation, onAttach, onOverride, onPersistTranslation, pendingTranslation,
}: Props) {
  const [logicFilter, setLogicFilter] = useState<LogicFilter>('all');

  return (
    <aside
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 520, maxWidth: '100vw',
        background: color.surface, borderLeft: `1px solid ${color.cardBorder}`, boxShadow: shadow.panel,
        display: 'flex', flexDirection: 'column', zIndex: 40, fontFamily: font.ui,
      }}
    >
      {/* Header */}
      <div style={{ padding: '18px 20px', borderBottom: `1px solid ${color.cardBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: color.ink, wordBreak: 'break-word' }}>{artifact.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: color.textMuted }}>{artifact.owner}</span>
              <CatChip cat={artifact.timing_cat} />
              <span style={{ fontSize: 12, color: color.textMuted }}>· {artifact.assignee}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button
              onClick={() => downloadText(`${artifact.name}.overview.md`, buildOverviewMarkdown(artifact))}
              title="Export a high-level overview (.md)"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none',
                border: `1px solid ${color.cardBorder}`, borderRadius: 8, cursor: 'pointer',
                color: color.textMuted, padding: '6px 10px', fontFamily: font.ui, fontSize: 12, fontWeight: 600,
              }}
            >
              <Download size={14} />
              Overview
            </button>
            {/* Per-EUC spec package (task 1218070214136694, subtask 8): the GCC-executable packet
                for this one EUC. Content differs by outcome (full spec / doc bundle / decommission
                record), with AC + tests + runbook drafts marked Draft. */}
            <button
              onClick={() => downloadText(`${artifact.name}.package.md`, buildSpecPackageMarkdown(artifact))}
              title="Export the per-EUC spec package (.md) — differs by disposition outcome"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                background: '#f59e0b', border: '1px solid #f59e0b', borderRadius: 8,
                color: '#fff', padding: '6px 10px', fontFamily: font.ui, fontSize: 12, fontWeight: 700,
              }}
            >
              <Download size={14} />
              Package
            </button>
            <button
              onClick={onClose}
              aria-label="Close panel"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: color.textMuted, padding: 4, lineHeight: 0 }}
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <OutcomePill value={artifact.outcome} confidence={artifact.confidence} detail={artifact.target_state_label} />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, padding: '0 12px', borderBottom: `1px solid ${color.cardBorder}` }}>
        {TABS.map((t) => {
          const active = panelTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onPanelTab(t.id)}
              style={{
                position: 'relative', padding: '12px 12px 11px', border: 'none', background: 'none', cursor: 'pointer',
                fontFamily: font.ui, fontSize: 13, fontWeight: active ? 700 : 600,
                color: active ? color.ink : color.textMuted,
                boxShadow: active ? `inset 0 -2px 0 ${color.accent}` : 'none',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {t.label}
              {/* §5.2: the Business logic tab carries a hardcode-count badge — the visual
                  signal that this artifact has values worth reviewing. */}
              {t.id === 'logic' && artifact.counts.hardcodes > 0 && (
                <span style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 999, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>
                  {artifact.counts.hardcodes}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {panelTab === 'overview' && <Overview artifact={artifact} onOverride={onOverride} onToast={onToast} />}
        {panelTab === 'lineage' && (
          needsFile(artifact)
            ? <NoFileNote what="Lineage" artifact={artifact} onAttach={onAttach} />
            : <Lineage artifact={artifact} />
        )}
        {panelTab === 'logic' && (
          needsFile(artifact) ? (
            <NoFileNote what="Business logic and translations" artifact={artifact} onAttach={onAttach} />
          ) : (
            <>
              <BusinessLogic artifact={artifact} logicFilter={logicFilter} onLogicFilter={setLogicFilter} />
              <TranslateBlock
                artifact={artifact}
                translateTab={translateTab}
                onTranslateTab={onTranslateTab}
                onToast={onToast}
                fetchTranslation={fetchTranslation}
                reviewTranslation={reviewTranslation}
                onPersist={onPersistTranslation}
                pending={pendingTranslation}
              />
            </>
          )
        )}
        {panelTab === 'insights' && <Insights artifact={artifact} />}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: color.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: color.ink, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

const TIMING_COORD: Record<1 | 2 | 3, string> = { 1: 'Cat 1 · Immediate', 2: 'Cat 2 · Near-term', 3: 'Cat 3 · Deferred' };

function Overview({ artifact, onOverride, onToast }: {
  artifact: ArtifactDetail;
  onOverride?: (eucId: string, outcome: Outcome, reason: string) => Promise<{ ok: boolean; error?: string }>;
  onToast: (msg: string) => void;
}) {
  return (
    <div>
      {/* Two coordinates (task 1218070214136694, subtask 8): a disposition is WHERE (the box) ×
          WHEN (the timing category), shown side by side so the consultant reads both axes, not a
          single collapsed pill. */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px', border: `1px solid ${color.cardBorder}`, borderRadius: 10, padding: '10px 12px', background: color.appBg }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: color.textMuted, textTransform: 'uppercase' }}>Where</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: color.ink, marginTop: 3 }}>{OUTCOME_LABEL[artifact.outcome]}</div>
          {artifact.target_state_label && <div style={{ fontSize: 12, color: color.textMuted, marginTop: 2 }}>{artifact.target_state_label}</div>}
        </div>
        <div style={{ flex: '1 1 140px', border: `1px solid ${color.cardBorder}`, borderRadius: 10, padding: '10px 12px', background: color.appBg }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: color.textMuted, textTransform: 'uppercase' }}>When</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: color.ink, marginTop: 3 }}>{TIMING_COORD[artifact.timing_cat]}</div>
          {artifact.confidence != null && <div style={{ fontSize: 12, color: color.textMuted, marginTop: 2 }}>Confidence {Math.round(artifact.confidence * 100)}%</div>}
        </div>
      </div>
      <Field label="Purpose">{artifact.purpose}</Field>
      <Field label="Why this box">{artifact.rationale || '—'}</Field>
      {!!artifact.gates_fired?.length && (
        <Field label={`Gates fired (${artifact.gates_fired.length})`}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {artifact.gates_fired.map((g) => (
              <span key={`${g.id}-${g.action}`} title={g.action} style={{
                fontFamily: font.mono, fontSize: 11, fontWeight: 700, color: color.purple,
                background: '#f5f3ff', border: '1px solid #e4dcff', borderRadius: 5, padding: '2px 6px',
              }}>
                {g.id} · {g.action}
              </span>
            ))}
          </div>
        </Field>
      )}
      <div style={{ display: 'flex', gap: 26 }}>
        <Field label="Last modified">{artifact.last_modified}</Field>
        <Field label="Usage">{artifact.usage_summary}</Field>
      </div>
      {onOverride && <SingleEucOverride artifact={artifact} onOverride={onOverride} onToast={onToast} />}
    </div>
  );
}

// Single-EUC override (task 1218070214136694, subtask 8): the same required-reason override the
// Review Queue does in bulk and the Framework Mapping worked example does per-EUC — surfaced here,
// on the panel a consultant lives in, WITHOUT duplicating commit logic (it calls the shared
// live.overrideDisposition endpoint passed down as onOverride).
function SingleEucOverride({ artifact, onOverride, onToast }: {
  artifact: ArtifactDetail;
  onOverride: (eucId: string, outcome: Outcome, reason: string) => Promise<{ ok: boolean; error?: string }>;
  onToast: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Outcome>(artifact.outcome);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const canSubmit = reason.trim().length >= 10 && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const res = await onOverride(artifact.id, target, reason.trim());
    setSaving(false);
    if (res.ok) { onToast(`Overrode ${artifact.id} → ${OUTCOME_LABEL[target]}.`); setOpen(false); setReason(''); }
    else onToast(`Override failed: ${res.error || 'unknown error'}`);
  };

  return (
    <div style={{ marginTop: 18, borderTop: `1px solid ${color.cardBorder}`, paddingTop: 14 }}>
      {!open ? (
        <button onClick={() => setOpen(true)} style={{
          fontSize: 12, fontWeight: 700, color: '#b45309', background: '#fffbeb',
          border: '1px solid #fcd34d', borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
        }}>
          Override disposition
        </button>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: color.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Override this EUC's box
          </div>
          <select value={target} onChange={(e) => setTarget(e.target.value as Outcome)}
            style={{ fontSize: 12, color: color.ink, background: color.surface, border: `1px solid ${color.cardBorder}`, borderRadius: 8, padding: '6px 8px' }}>
            {OUTCOME_ORDER.map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
          </select>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder="Why (min. 10 characters — required, this is an audit trail)"
            style={{ fontSize: 12, color: color.ink, background: color.surface, border: `1px solid ${color.cardBorder}`, borderRadius: 8, padding: '6px 8px', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={submit} disabled={!canSubmit} style={{
              cursor: canSubmit ? 'pointer' : 'default', opacity: canSubmit ? 1 : 0.5,
              fontSize: 12, fontWeight: 700, color: '#fff', background: '#f59e0b', border: 'none', borderRadius: 8, padding: '6px 12px',
            }}>
              {saving ? 'Saving…' : 'Confirm override'}
            </button>
            <button onClick={() => { setOpen(false); setReason(''); }} style={{
              fontSize: 12, color: color.textMuted, background: 'transparent', border: 'none', cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// A file analyzed via the per-file "Analyze a file" upload has its Business Logic extracted, but
// lineage / rationale / insights come from the heavier full-ingest run — so those tabs are honestly
// marked pending rather than shown empty as if nothing was found.
const isAnalyzedOnly = (a: ArtifactDetail) => a.id.startsWith('analyzed:');

const needsFile = (a: ArtifactDetail) => a.file_attached === false;

function NoFileNote({ what, artifact, onAttach }: { what: string; artifact: ArtifactDetail; onAttach?: (id: string) => void }) {
  return (
    <div style={{
      border: `1px dashed ${color.cardBorder}`, borderRadius: 12, padding: '22px 18px',
      textAlign: 'center', background: color.appBg,
    }}>
      <Upload size={22} style={{ color: color.textMuted, marginBottom: 8 }} />
      <div style={{ fontSize: 13.5, fontWeight: 700, color: color.ink, marginBottom: 5 }}>
        No file attached
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 12.5, color: color.textMuted, lineHeight: 1.55 }}>
        {what} needs the source file. Attach the source file(s) for this EUC to extract formulas, lineage and logic.
      </p>
      <button
        onClick={() => onAttach?.(artifact.id)}
        disabled={!onAttach}
        style={{
          cursor: onAttach ? 'pointer' : 'default', fontSize: 12.5, fontWeight: 700, color: '#fff',
          background: color.accent, border: 'none', borderRadius: 9, padding: '9px 16px', fontFamily: font.ui,
        }}
      >
        Attach source file
      </button>
      <div style={{ fontSize: 12, color: color.textMuted, marginTop: 10 }}>
        .xlsx · .xlsm · .yxmd · .dsx · .xml · .tflx · .twbx
      </div>
    </div>
  );
}

function PendingNote({ what }: { what: string }) {
  return (
    <div style={{
      fontSize: 12.5, color: '#B45309', background: '#FEF3C7', border: '1px solid #FCD34D',
      borderRadius: 8, padding: '9px 12px', marginBottom: 14, lineHeight: 1.5,
    }}>
      {what} arrives with the full extraction run — this view shows the file’s parsed logic only.
    </div>
  );
}

function Lineage({ artifact }: { artifact: ArtifactDetail }) {
  const noLineage = artifact.sources.length === 0 && artifact.targets.length === 0 && artifact.transform_count === 0;
  return (
    <div>
      {isAnalyzedOnly(artifact) && noLineage && <PendingNote what="Lineage (sources → transforms → targets)" />}
      <Field label={`Sources (${artifact.sources.length})`}>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {artifact.sources.map((s) => <li key={s} style={{ marginBottom: 3 }}>{s}</li>)}
        </ul>
      </Field>
      <Field label={`Transforms (${artifact.transform_count})`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {artifact.transforms.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'baseline' }}>
              <span style={{
                fontFamily: font.mono, fontSize: 11, fontWeight: 700, color: color.purple,
                background: '#f5f3ff', border: '1px solid #e4dcff', borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap',
              }}>
                {t.op}
              </span>
              <span style={{ fontSize: 12.5, color: color.ink }}>{t.desc}</span>
            </div>
          ))}
        </div>
        {artifact.transforms.length < artifact.transform_count && (
          <div style={{ fontSize: 12, color: color.textMuted, marginTop: 8 }}>
            Showing {artifact.transforms.length} of {artifact.transform_count}.
          </div>
        )}
      </Field>
      <Field label={`Targets (${artifact.targets.length})`}>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {artifact.targets.map((t) => <li key={t} style={{ marginBottom: 3 }}>{t}</li>)}
        </ul>
      </Field>
    </div>
  );
}

function BusinessLogic({
  artifact, logicFilter, onLogicFilter,
}: { artifact: ArtifactDetail; logicFilter: LogicFilter; onLogicFilter: (f: LogicFilter) => void }) {
  const rows = useMemo(
    () => artifact.logic_rows.filter((r) => logicFilter === 'all' || r.tag.toLowerCase() === logicFilter),
    [artifact.logic_rows, logicFilter],
  );
  const c = artifact.counts;

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 4, flexWrap: 'wrap', fontSize: 12, color: color.textMuted }}>
        <span><b style={{ color: color.ink }}>{c.formulas}</b> formulas</span>
        <span><b style={{ color: color.ink }}>{c.macros}</b> macros</span>
        <span><b style={{ color: '#dc2626' }}>{c.hardcodes}</b> hardcodes</span>
        <span><b style={{ color: color.ink }}>{c.lookups}</b> lookups</span>
        <span><b style={{ color: color.ink }}>{c.distinct_rules}</b> distinct rules</span>
      </div>
      {/* `formulas` counts every cell, including a rule filled down 100s of rows; `distinct
          rules` is what's actually listed below (fill-downs collapse to one row with a ×N
          badge). Spelled out so the two numbers next to each other never read as a mismatch
          again (QA finding 2026-08-03). */}
      {c.formulas + c.macros > c.distinct_rules && (
        <div style={{ fontSize: 11, color: color.textMuted, marginBottom: 10 }}>
          Repeated formulas are grouped into one row each — {c.distinct_rules} distinct rule{c.distinct_rules === 1 ? '' : 's'} cover all {c.formulas + c.macros} statement{c.formulas + c.macros === 1 ? '' : 's'} in the file.
        </div>
      )}
      {(artifact.truncated || 0) > 0 && (
        <div style={{
          fontSize: 12, color: '#B45309', background: '#FEF3C7', border: '1px solid #FCD34D',
          borderRadius: 8, padding: '7px 10px', marginBottom: 10,
        }}>
          {artifact.truncated} more distinct rule{artifact.truncated === 1 ? '' : 's'} not shown — this view is capped for readability.
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {LOGIC_FILTERS.map((f) => {
          const active = logicFilter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => onLogicFilter(f.id)}
              style={{
                padding: '4px 10px', borderRadius: radius.chip, cursor: 'pointer',
                border: `1px solid ${active ? color.accent : color.cardBorder}`,
                background: active ? '#f5f7ff' : color.surface,
                color: active ? color.accent : color.textMuted,
                fontFamily: font.ui, fontSize: 12, fontWeight: 600,
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div style={{ background: color.ink, borderRadius: radius.card, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span
              style={{
                fontFamily: font.mono, fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 6px', marginTop: 2,
                background: r.tag === 'HC' ? '#ff6b8a20' : '#7c9dff20',
                color: r.tag === 'HC' ? '#ff6b8a' : '#7c9dff',
              }}
            >
              {r.tag}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, color: '#8d8bb0', marginBottom: 4 }}>
                {r.location}
                {(r.repeat_count || 1) > 1 && (
                  <span
                    title={`This rule appears in ${r.repeat_count} cells — grouped into one row.`}
                    style={{ marginLeft: 7, fontFamily: font.mono, fontSize: 11, color: '#c9c8e0', background: '#ffffff14', borderRadius: 4, padding: '1px 5px' }}
                  >
                    ×{r.repeat_count}
                  </span>
                )}
              </div>
              {/* §4.2: code_html is server-rendered highlighted HTML — "Sanitize with
                  DOMPurify on the client before injection." */}
              <div
                style={{ fontFamily: font.mono, fontSize: 12, color: '#e6e6f2', lineHeight: 1.6, wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.code_html) }}
              />
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div style={{ color: '#8d8bb0', fontSize: 12, padding: '10px 2px' }}>No logic rows match this filter.</div>
        )}
      </div>
    </div>
  );
}

function Insights({ artifact }: { artifact: ArtifactDetail }) {
  const noInsights = artifact.signals.length === 0 && artifact.overlaps.length === 0;
  return (
    <div>
      {isAnalyzedOnly(artifact) && noInsights && <PendingNote what="Signals & overlap insights" />}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        {artifact.signals.map((s) => (
          <div
            key={s.label}
            title={s.tooltip}
            style={{
              border: `1px solid ${color.cardBorder}`, borderRadius: radius.card, padding: '12px 14px',
              background: '#fcfcfe', cursor: 'help',
            }}
          >
            <div style={{ fontSize: 11, color: color.textMuted, fontWeight: 600, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: SIGNAL_COLOR[s.level] }}>{s.value}</div>
          </div>
        ))}
      </div>

      <Field label="Overlapping artifacts">
        {artifact.overlaps.length === 0 ? (
          <span style={{ color: color.textMuted }}>No overlapping artifacts found.</span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {artifact.overlaps.map((o) => (
              <div key={o.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 12.5, color: color.ink, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.name}
                </span>
                <span style={{ fontFamily: font.mono, fontSize: 12, fontWeight: 700, color: o.similarity >= 0.85 ? '#dc2626' : color.textMuted }}>
                  {o.similarity.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Field>
    </div>
  );
}
